import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  /** @type {Map<string, string>} Cache for color variant metafield URLs */
  #colorMetafieldCache = new Map();

  /** @type {string | null} Store the slide ID before swatch hover to restore it later */
  #slideIdBeforeSwatchHover = null;

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    // Cache color metafield data on initial load
    this.#cacheColorMetafields();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Bind swatch events on initial load
    this.#rebindSwatchEvents();

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });

    // Check initial variant availability to ensure button state is correct on load
    // Use multiple attempts to handle deferred content (like overflow-list)
    let retryCount = 0;
    const maxRetries = 10; // Maximum 10 retries (500ms total)
    
    const checkAvailability = () => {
      const hasInputs = this.querySelectorAll('input[type="radio"][data-option-available]').length > 0;
      const hasButton = this.querySelector('.quick-add__button') || this.querySelector('add-to-cart-component button');
      
      if (hasInputs || hasButton || retryCount >= maxRetries) {
        this.#checkInitialVariantAvailability();
      } else {
        // Retry after a short delay if inputs/button not found yet
        retryCount++;
        setTimeout(checkAvailability, 50);
      }
    };
    
    // Start checking after a short delay to allow DOM to settle
    requestAnimationFrame(() => {
      setTimeout(checkAvailability, 0);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Caches color metafield data from all color inputs on initial load
   */
  #cacheColorMetafields() {
    try {
      if (!this.variantPicker) return;

      // Find all color inputs
      const colorInputs = this.variantPicker.querySelectorAll('input[name*="Color"], input[name*="color"], input[name*="颜色"]');
      
      for (const input of colorInputs) {
        const colorValue = input.value;
        const metafieldUrls = input.dataset.customTestMediaUrls;
        
        if (colorValue && metafieldUrls) {
          this.#colorMetafieldCache.set(colorValue, metafieldUrls);
        }
      }
    } catch (e) {
      console.warn('Error caching color metafields:', e);
    }
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Clear the stored slide ID when variant changes
    // This ensures the next swatch hover will store the newly selected variant's image
    this.#slideIdBeforeSwatchHover = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
    
    // Re-bind pointerleave event after variant update (in case HTML was replaced)
    // Use requestAnimationFrame to ensure DOM has been fully updated
    requestAnimationFrame(() => {
      this.#rebindSwatchEvents();
    });
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Re-binds swatch hover events after variant picker HTML is replaced
   */
  #rebindSwatchEvents() {
    // Find all swatch fieldsets
    const fieldsets = this.querySelectorAll('.variant-option--swatches');
    
    fieldsets.forEach(fieldset => {
      // Remove old listener if exists
      fieldset.removeEventListener('pointerleave', this.#handleFieldsetPointerLeave);
      
      // Add new listener
      fieldset.addEventListener('pointerleave', this.#handleFieldsetPointerLeave);
    });
  }

  /**
   * Handler for fieldset pointerleave event
   */
  #handleFieldsetPointerLeave = () => {
    this.resetSwatchHover();
  };

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainers = this.querySelectorAll(`product-price [ref='priceContainer']`);
    const priceContainer = priceContainers[1] || priceContainers[0];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    if (allVariants.length === 0) return;

    // Check if all selected options are available
    let allAvailable = true;
    for (const variant of allVariants) {
      if (variant.dataset.optionAvailable !== 'true') {
        allAvailable = false;
        break;
      }
    }

    this.#toggleAddToCartButton(allAvailable);
  }

  /**
   * Checks the initial variant availability when the component is connected.
   * This ensures the add to cart button is disabled if all variants are out of stock on initial load.
   */
  #checkInitialVariantAvailability() {
    const checkedInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input[type="radio"]:checked[data-option-available]')
    );

    // If there are checked inputs, check their availability
    if (checkedInputs.length > 0) {
      let allAvailable = true;
      for (const input of checkedInputs) {
        if (input.dataset.optionAvailable !== 'true') {
          allAvailable = false;
          break;
        }
      }
      this.#toggleAddToCartButton(allAvailable);
      return;
    }

    // If no inputs are checked, check if all available inputs are unavailable
    // This handles the case where no swatch is pre-selected but all variants are out of stock
    const allInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input[type="radio"][data-option-available]')
    );

    if (allInputs.length === 0) return;

    // Check if all inputs are unavailable
    let allUnavailable = true;
    for (const input of allInputs) {
      if (input.dataset.optionAvailable === 'true') {
        allUnavailable = false;
        break;
      }
    }

    // If all inputs are unavailable, disable the button
    if (allUnavailable) {
      this.#toggleAddToCartButton(false);
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    // Try to find quick-add button first (for product cards)
    let addToCartButton = this.querySelector('.quick-add__button');
    
    // If not found, try to find add-to-cart button (for product forms)
    if (!addToCartButton) {
      addToCartButton = this.querySelector('add-to-cart-component button');
    }

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    // Use the same logic as getCurrentVariantMediaId to get the media ID
    const selectedImageId = this.#getCurrentVariantMediaId();

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        // Skip temporary hover slides - they manage their own visibility
        if (slide.getAttribute('data-temp-hover') === 'true') continue;
        
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    // 按优先级查找变体选择器
    return this.querySelector('variant-picker') ||
           this.querySelector('swatches-variant-picker-component') || 
           this.querySelector('product-variant-picker-card variant-picker');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // Store the selected variant's media ID before swatch hover (only if not already stored)
    if (!this.#slideIdBeforeSwatchHover) {
      // Get the currently selected variant's media ID instead of current slide
      const selectedMediaId = this.#getCurrentVariantMediaId();
      if (selectedMediaId) {
        this.#slideIdBeforeSwatchHover = selectedMediaId;
      } else {
        // Fallback to current slide if no variant is selected
        const currentSlide = slideshow.refs.slides[slideshow.current];
        if (currentSlide) {
          this.#slideIdBeforeSwatchHover = currentSlide.getAttribute('slide-id');
        }
      }
    }

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Resets to the image that was showing before swatch hover
   */
  resetSwatchHover() {
    const { slideshow } = this.refs;
    
    if (!slideshow) return;

    this.resetVariant.cancel();

    // Restore the slide that was showing before swatch hover
    if (this.#slideIdBeforeSwatchHover) {
      slideshow.select({ id: this.#slideIdBeforeSwatchHover }, undefined, { animate: false });
      this.#slideIdBeforeSwatchHover = null; // Clear the stored ID
    }
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;
    if (!slideshow) return;

    // Cancel any pending reset
    this.resetVariant.cancel();

    // Get URLs from metafield
    const mediaUrls = this.#getMetafield('custom.test_media_urls');
    if (mediaUrls) {
      const urls = mediaUrls.split(',');
      const firstUrl = urls[0]?.trim();
      
      if (firstUrl) {
        this.#createSlideWithUrl('hover-url-' + Date.now(), firstUrl);
        return;
      }
    }

    // No hover image found, stay on current image
  }

  /**
   * Creates a temporary slide for hover image
   * @param {string} imageId - The image ID
   * @returns {HTMLElement | null} The created slide element
   */
  #createHoverSlide(imageId) {
    try {
      // First, try to get the image URL from Shopify API
      this.#fetchImageUrl(imageId).then(imageUrl => {
        if (imageUrl) {
          this.#createSlideWithUrl(imageId, imageUrl);
        }
      });
      
      return null; // Will be created asynchronously
    } catch (e) {
      console.warn('🔍 HOVER: Error creating hover slide:', e);
      return null;
    }
  }

  /**
   * Fetches the actual image URL from Shopify
   * @param {string} imageId - The image ID
   * @returns {Promise<string|null>} The image URL or null
   */
  async #fetchImageUrl(imageId) {
    try {
      // Try to get product data first
      const productUrl = this.refs.productCardLink?.href;
      if (!productUrl) {
        return null;
      }

      // Clean the URL and try different approaches
      const baseUrl = productUrl.split('?')[0]; // Remove query parameters
      const jsonUrl = baseUrl + '.json';
      
      try {
        const response = await fetch(jsonUrl);
        if (!response.ok) {
          return this.#constructImageUrl(imageId);
        }
        
        const data = await response.json();
        
        // Find the image in product media
        if (data.product && data.product.media) {
          const media = data.product.media.find(m => m.id.toString() === imageId);
          if (media && media.preview_image && media.preview_image.src) {
            return media.preview_image.src;
          }
        }
        
        // Also try images array
        if (data.product && data.product.images) {
          const image = data.product.images.find(img => img.id.toString() === imageId);
          if (image && image.src) {
            return image.src;
          }
        }
        
        return this.#constructImageUrl(imageId);
        
      } catch (fetchError) {
        return this.#constructImageUrl(imageId);
      }
      
    } catch (e) {
      return this.#constructImageUrl(imageId);
    }
  }

  /**
   * Constructs image URL using Shopify CDN pattern
   * @param {string} imageId - The image ID
   * @returns {string} Constructed image URL
   */
  #constructImageUrl(imageId) {
    return `https://placeholder.invalid/${imageId}`;
  }

  /**
   * Creates slide with the actual image URL
   * @param {string} imageId - The image ID
   * @param {string} imageUrl - The image URL
   */
  #createSlideWithUrl(imageId, imageUrl) {
    try {
      const { slideshow } = this.refs;
      if (!slideshow) return;

      // Ensure URL has protocol
      let fullUrl = imageUrl;
      if (imageUrl.startsWith('//')) {
        fullUrl = 'https:' + imageUrl;
      }

      // Check if slide already exists (avoid duplicates)
      const existingSlide = slideshow.refs.slides?.find(slide => 
        slide.getAttribute('slide-id') === imageId
      );
      if (existingSlide) {
        // Hide all other slides
        slideshow.refs.slides.forEach(s => {
          if (s !== existingSlide) {
            s.setAttribute('aria-hidden', 'true');
            s.hidden = true;
          }
        });
        
        // Show the existing hover slide
        existingSlide.setAttribute('aria-hidden', 'false');
        existingSlide.hidden = false;
        return;
      }

      // Create slide element
      const slide = document.createElement('slideshow-slide');
      slide.setAttribute('ref', 'slides[]');
      slide.setAttribute('slide-id', imageId);
      slide.className = 'product-media-container media-fit product-media-container--image';
      slide.setAttribute('data-temp-hover', 'true'); // Mark as temporary
      
      // Create media container
      const mediaContainer = document.createElement('div');
      mediaContainer.className = 'product-media';
      mediaContainer.style.setProperty('--ratio', '1.0');
      mediaContainer.setAttribute('data-media-id', imageId);
      
      // Create image element
      const img = document.createElement('img');
      img.src = fullUrl;
      img.alt = 'Hover image';
      img.className = 'product-media__image';
      img.loading = 'eager'; // Load immediately for hover
      
      mediaContainer.appendChild(img);
      slide.appendChild(mediaContainer);
      
      // Insert after current slide
      const currentSlide = slideshow.refs.slides[slideshow.current];
      if (currentSlide) {
        currentSlide.after(slide);
        
        // Add to slideshow refs
        const currentIndex = slideshow.current;
        slideshow.refs.slides.splice(currentIndex + 1, 0, slide);
        
        // CRITICAL: Manually control visibility
        // Hide ALL slides first
        slideshow.refs.slides.forEach(s => {
          s.setAttribute('aria-hidden', 'true');
          s.hidden = true;
        });
        
        // Show ONLY the hover slide
        slide.setAttribute('aria-hidden', 'false');
        slide.hidden = false;
      }
    } catch (e) {
      console.warn('Error creating slide with URL:', e);
    }
  }

  /**
   * Hides the current variant image when showing hover image
   */
  #hideCurrentVariantImage() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const currentSlide = slideshow.refs.slides[slideshow.current];
    if (currentSlide && currentSlide.getAttribute('data-temp-hover') !== 'true') {
      currentSlide.hidden = true;
    }
  }

  /**
   * Shows the variant image when hover ends
   */
  #showVariantImage() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    // Get the selected variant's media ID
    const selectedMediaId = this.#getCurrentVariantMediaId();
    
    if (selectedMediaId) {
      // Find and show the variant image
      const variantSlide = slideshow.refs.slides?.find(slide => 
        slide.getAttribute('slide-id') === selectedMediaId &&
        slide.getAttribute('data-temp-hover') !== 'true'
      );
      
      if (variantSlide) {
        variantSlide.hidden = false;
      }
    }
  }

  /**
   * Gets the first image URL from variant metafield for hover
   * @returns {string | null} Image URL or null
   */
  #getVariantHoverImageUrl() {
    try {
      // Try to get the URLs directly from Liquid template
      const metafieldUrls = this.#getMetafield('custom.test_media_urls');
      if (metafieldUrls) {
        const urls = metafieldUrls.split(',');
        if (urls.length > 0) {
          const firstUrl = urls[0].trim();
          return firstUrl;
        }
      }
      
      return null;
    } catch (e) {
      console.warn('Error parsing hover image:', e);
      return null;
    }
  }

  /**
   * Gets product data from the page's JSON script
   * @returns {object|null} Product data or null if not found
   */
  #getProductData() {
    try {
      // Try to find product data in various possible locations
      const productScripts = document.querySelectorAll('script[type="application/json"]');
      
      for (const script of productScripts) {
        try {
          const data = JSON.parse(script.textContent);
          // Check if this looks like product data
          if (data && (data.variants || data.media || data.images)) {
            return data;
          }
        } catch (e) {
          continue;
        }
      }
      
      // Try window.product if available
      if (window.product) {
        return window.product;
      }
      
      return null;
    } catch (e) {
      console.warn('Could not get product data:', e);
      return null;
    }
  }

  /**
   * Gets a metafield value from variant data
   * @param {string} key - The metafield key (e.g., 'custom.test_media')
   * @returns {any} The metafield value or null if not found
   */
  #getMetafield(key) {
    try {
      // Get the currently selected color option
      const colorInput = this.#getCurrentColorInput();
      
      if (colorInput) {
        const colorValue = colorInput.value;
        
        // First try to get from cache
        if (this.#colorMetafieldCache.has(colorValue)) {
          const cachedUrls = this.#colorMetafieldCache.get(colorValue);
          return cachedUrls;
        }
        
        // Fallback: try to read from current input's dataset
        const variations = [
          key.replace('.', '-'),
          key.replace(/[.-]/g, ''),
          'customTestMedia',
          'customTestMediaUrls',
          'customtestmediaurls'
        ];
        
        for (const variation of variations) {
          if (colorInput.dataset[variation]) {
            return colorInput.dataset[variation];
          }
        }
      }

      return null;
    } catch (e) {
      console.warn('Error reading metafield:', e);
      return null;
    }
  }

  /**
   * Gets the currently selected color input (not specific variant)
   * @returns {HTMLInputElement | null} The color input element
   */
  #getCurrentColorInput() {
    try {
      if (!this.variantPicker) return null;

      // Find the color option fieldset
      let colorFieldset = this.variantPicker.querySelector('fieldset[data-option-id*="Color"], fieldset[data-option-id*="color"]');
      
      // 如果没找到，尝试其他可能的属性
      if (!colorFieldset) {
        colorFieldset = this.variantPicker.querySelector('fieldset[data-option-id*="颜色"]');
      }
      
      // 如果还没找到，尝试查找包含颜色相关name的input的父fieldset
      if (!colorFieldset) {
        const colorInput = this.variantPicker.querySelector('input[name*="Color"], input[name*="color"], input[name*="颜色"]');
        if (colorInput) {
          colorFieldset = colorInput.closest('fieldset');
        }
      }

      if (!colorFieldset) return null;

      // Find the checked input in the color fieldset
      const checkedColorInput = colorFieldset.querySelector('input[type="radio"]:checked');
      return checkedColorInput || null;
    } catch (e) {
      console.warn('Error getting color input:', e);
      return null;
    }
  }

  /**
   * Gets the currently selected variant's media ID
   * @returns {string | null} The media ID of the selected variant
   */
  #getCurrentVariantMediaId() {
    // Try to get from variant picker
    if (this.variantPicker) {
      // First try to find color option with media ID (original approach)
      let colorInput = /** @type {HTMLInputElement | null} */ (
        this.variantPicker.querySelector('input[type="radio"]:checked[data-option-media-id]')
      );
      
      if (colorInput) {
        return colorInput.dataset.optionMediaId || null;
      }
      
      // Fallback: find any checked color input and get media ID from label
      const checkedColorInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
        this.variantPicker.querySelectorAll('input[type="radio"]:checked')
      );
      for (const input of checkedColorInputs) {
        // Check if this is a color input (has Color in the name)
        if (input.name && input.name.includes('Color')) {
          // Try to get media ID from the parent label
          const label = input.closest('label');
          const mediaId = label?.getAttribute('data-media-id');
          if (mediaId) {
            return mediaId;
          }
        }
      }
    }

    // Final fallback: try to find any checked input with media ID in the entire component
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-option-media-id]')
    );
    return checkedInput?.dataset.optionMediaId || null;
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // IMPORTANT: Get the target slide ID BEFORE removing temporary slides
    // This ensures we know which slide to restore to
    const selectedMediaId = this.#getCurrentVariantMediaId();
    
    // Determine which slide to restore to
    let targetSlideId = null;
    if (selectedMediaId) {
      targetSlideId = selectedMediaId;
    } else {
      // No variant selected - use initial slide
      const initialSlide = slideshow.initialSlide;
      targetSlideId = initialSlide?.getAttribute('slide-id');
    }

    // Remove any temporary hover slides
    const tempSlides = slideshow.refs.slides?.filter(slide => 
      slide.getAttribute('data-temp-hover') === 'true'
    );
    
    if (tempSlides && tempSlides.length > 0) {
      tempSlides.forEach(slide => {
        const index = slideshow.refs.slides.indexOf(slide);
        if (index > -1) {
          slideshow.refs.slides.splice(index, 1);
        }
        slide.remove();
      });
    }

    // Show the variant image again (unhide it)
    this.#showVariantImage();

    // Now restore to the target slide
    if (targetSlideId) {
      slideshow.select({ id: targetSlideId }, undefined, { animate: false });
      return;
    }

    // Final fallback - go to previous slide
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = /** @type {HTMLInputElement} */ (event.target);
    const availableCount = parseInt(clickedSwatch.dataset?.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset?.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
